
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Typography, Select, MenuItem, FormControl, Box, FormHelperText, TextField, Button, Autocomplete, FormControlLabel, Checkbox, Collapse, IconButton } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Icon } from "@iconify/react";
import { AntSwitch } from '../../../components/antSwitch';
import { ecosytemIntegatorMetaData, ecosytemIntegatorTypesWithIds, ecosytemIntegatorCreateOrUpdate, ecosytemIntegatorGetById, getInitiativeOptions, getIntegratorInitiatives, getIntegratorSubUsers, addIntegratorSubUser, removeIntegratorSubUser } from '../ecosystemPartners.service';
import { getAllcountryManagement } from '../../countryManagement/countryManagement.service';
import { getInitiativeRedirects } from '../../settings/integratorRedirects/integratorRedirects.service';
import { getAllCrops, linkCropToIntegrator, unlinkCropFromIntegrator, getCropsByIntegrator } from '../../crops/crops.service';
import Loader from '../../../components/loader';
import LinkedAccounts from './linkedAccounts';
import AlertToast from '../../../components/alertToast';
import FlagImage from '../../../components/flagImage/FlagImage';
import { EcosytemIntegratorConstants } from '../../../constant/ecosystem/ecosystemConst';
import { PHONE_MAX_LENGTH } from '../../../constant/phone';
import { ecosystemIntegratorSchema } from '../validationschema';
import {
  createMenuProps,
  FieldLabel,
  formFieldSx,
  helperTextSx,
  placeholderTextSx,
  sectionTitleSx,
} from '../../../styles/formStyles';
import { primaryIconColor } from '../../../styles/iconStyles';
import {
  createEditBackButtonSx,
  createEditCancelButtonSx,
  createEditCardSx,
  createEditFooterSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageSubtitleSx,
  createEditPageTitleSx,
  createEditPrimaryButtonSx,
} from '../../../styles/pageFormStyles';

const MenuProps = createMenuProps();
const fieldSx = formFieldSx;

// Phone length is uniform across the platform (digits only, fixed range) and no
// longer tied to the selected country.
const getPhoneNumberMaxLength = () => PHONE_MAX_LENGTH;

export const EcosystemPartnersCreation = () => {
  const location = useLocation();
  const { id } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const type = queryParams.get("type");
  const { ECOSYSTEMCREATION } = EcosytemIntegratorConstants()
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [countryCodeData, setCountryCodeData] = useState([]);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState("");
  const [integatorType, setIntegatorType] = useState([]);
  const [integratorTypeMap, setIntegratorTypeMap] = useState({}); // Maps type name to ID
  const [phoneNumberMaxLength, setPhoneNumberMaxLength] = useState(PHONE_MAX_LENGTH);
  const [cropsList, setCropsList] = useState([]);
  const [selectedCrops, setSelectedCrops] = useState([]);
  const [originalLinkedCrops, setOriginalLinkedCrops] = useState([]);
  const [cropsExpanded, setCropsExpanded] = useState(false);
  const [subUsers, setSubUsers] = useState([]);
  const [subUserForm, setSubUserForm] = useState({ name: '', email: '', phoneNumber: '', countryCode: '' });
  const [subUserErrors, setSubUserErrors] = useState({});
  const [subUserAdding, setSubUserAdding] = useState(false);
  const [initiativeOptions, setInitiativeOptions] = useState([]);
  const [initiativeOptionsLoading, setInitiativeOptionsLoading] = useState(false);
  // Edit mode: the Initiatives the integrator is on when the form opens (null until loaded).
  const [currentInitiatives, setCurrentInitiatives] = useState(null);
  const [initiativeRedirects, setInitiativeRedirects] = useState([]);
  const [integratorTypes, setIntegratorTypes] = useState([]);
  const ALERT_DURATION = 2000;

  // Last-resort ids, from the baseline seed. Only used if the types endpoint cannot be
  // reached — an environment seeded in a different order has different ids, and a wrong
  // one does not fail, it silently leaves the type as it was.
  const INTEGRATOR_TYPE_IDS = {
    'AREA_BASED': 1,
    'CROP_BASED': 2,
    'INITIATIVE_BASED': 3,
    'FINANCE_BASED': 4
  };

  const alertClose = () => {
    setAlert('')
  }

  const { handleSubmit, formState: { errors }, control, setValue, setError, clearErrors, watch } = useForm({
    resolver: zodResolver(ecosystemIntegratorSchema)
  });
  const selectedIntegratorType = watch('integratorType');
  const showRedirectUrl = selectedIntegratorType === 'INITIATIVE_BASED' || selectedIntegratorType === 'FINANCE_BASED';
  const selectedCountryId = watch('countryId');
  // An Initiative Based integrator serves one Initiative of its country: picked on create, and on
  // edit changing it moves the integrator there. One on several Initiatives is managed on each
  // Initiative's own page instead, so the field is read-only for it.
  const showInitiativePicker = selectedIntegratorType === 'INITIATIVE_BASED';
  const originalInitiative = editMode && currentInitiatives?.length === 1 ? currentInitiatives[0] : null;
  const initiativeLocked = editMode && (currentInitiatives?.length ?? 0) > 1;

  // A new Area Based integrator starts Open: DAREs and farmers in its area join it at sign-up,
  // which only Open integrators allow. The others start Closed. Edit keeps the stored value.
  useEffect(() => {
    if (editMode || !selectedIntegratorType) return;
    setValue('isOpen', selectedIntegratorType === 'AREA_BASED');
  }, [editMode, selectedIntegratorType, setValue]);

  useEffect(() => {
    if (!showInitiativePicker || !selectedCountryId) {
      setInitiativeOptions([]);
      return undefined;
    }
    let cancelled = false;
    setInitiativeOptionsLoading(true);
    getInitiativeOptions(selectedCountryId).then((options) => {
      if (cancelled) return;
      // Keep the integrator's current Initiative selectable even if it is no longer offered here.
      const missing = (currentInitiatives || []).filter((item) => !options.some((option) => option.id === item.id));
      setInitiativeOptions([...options, ...missing]);
      setInitiativeOptionsLoading(false);
    });
    return () => { cancelled = true; };
  }, [showInitiativePicker, selectedCountryId, currentInitiatives]);

  // The redirect a new Initiative Based integrator inherits: its Initiative's URL for open or
  // closed integrators (Settings → Redirect Configuration), else the Initiative Based type URL.
  useEffect(() => {
    if (!showInitiativePicker) return undefined;
    let cancelled = false;
    getInitiativeRedirects().then((result) => {
      if (!cancelled && result.success && Array.isArray(result.data)) {
        setInitiativeRedirects(result.data);
      }
    });
    return () => { cancelled = true; };
  }, [showInitiativePicker]);

  const selectedInitiativeId = watch('initiativeId');
  const selectedIsOpen = watch('isOpen');
  const inheritedRedirect = (() => {
    const initiative = initiativeRedirects.find((item) => item.initiativeId === selectedInitiativeId);
    const fromInitiative = initiative ? (selectedIsOpen ? initiative.openAppUrl : initiative.closedAppUrl) : null;
    if (fromInitiative) {
      return { url: fromInitiative, source: `${initiative.name}, ${selectedIsOpen ? 'open' : 'closed'} integrators` };
    }
    const typeUrl = integratorTypes.find((type) => type.category === 'INITIATIVE_BASED')?.appUrl;
    return typeUrl ? { url: typeUrl, source: 'Initiative Based type' } : null;
  })();

  const fetchData = useCallback(async () => {
    if (editMode !== true || countryData.length === 0) return;

    const res = await ecosytemIntegatorGetById(id);
    const ecosystemData = res?.data || res;

    setValue('ecosystemIntegratorName', ecosystemData?.integratorName || '');
    setValue('countryId', ecosystemData?.countryId || '');
    const rawType = ecosystemData?.integratorType || '';
    // Backend may return display name ("Crop Based") instead of enum code ("CROP_BASED")
    const typeCode = rawType.includes(' ') ? rawType.toUpperCase().replace(/\s+/g, '_') : rawType;
    setValue('integratorType', typeCode);
    setValue('email', ecosystemData?.email || '');
    setValue('phoneNumber', ecosystemData?.phoneNumber || '');
    setValue('isOpen', ecosystemData?.isOpen === true);
    setValue('appUrl', ecosystemData?.appUrl || '');

    // Resolve countryCode: use backend value or derive from country's phoneCode
    let countryCode = ecosystemData?.countryCode || '';
    if (!countryCode && ecosystemData?.countryId) {
      const country = countryData.find(c => c.id === ecosystemData.countryId);
      countryCode = country?.phoneCode || '';
    }
    setValue('countryCode', countryCode);
    setPhoneNumberMaxLength(getPhoneNumberMaxLength(countryCode));

    // The Initiative(s) it is on: one fills the picker; several make it read-only.
    const initiatives = await getIntegratorInitiatives(id);
    setCurrentInitiatives(initiatives);
    setValue('initiativeId', initiatives.length === 1 ? initiatives[0].id : '');

    // Load crops already linked to this integrator
    try {
      const linked = await getCropsByIntegrator(id);
      setSelectedCrops(linked);
      setOriginalLinkedCrops(linked);
    } catch (err) {
      console.error('Error loading linked crops:', err);
    }

    // Load sub-users already attached to this integrator
    try {
      const subs = await getIntegratorSubUsers(id);
      setSubUsers((subs || []).map(s => ({
        id: s.id,
        name: s.integratorName,
        email: s.email,
        phoneNumber: s.phoneNumber,
        countryCode: s.countryCode,
      })));
    } catch (err) {
      console.error('Error loading sub-users:', err);
    }
  }, [editMode, id, setValue, countryData]);

  const ecosytemMetaData = useCallback(async () => {
    const res = await ecosytemIntegatorMetaData();
    if (res.statusCode === 200) {
      setIntegatorType(res.data.integratorTypeList);
    }

    // The ids come from the types endpoint, not from the hardcoded table above. The old
    // map was built as `typeMap[code] = INTEGRATOR_TYPE_IDS[code]`, so it only ever
    // restated the guess under a different name and could not correct it.
    const types = await ecosytemIntegatorTypesWithIds();
    const typeMap = {};
    setIntegratorTypes(types);
    types.forEach((type) => {
      if (type?.category && type?.id != null) {
        typeMap[type.category] = type.id;
      }
    });
    setIntegratorTypeMap(typeMap);
  }, [])

  const countriesData = useCallback(async () => {
    getAllcountryManagement(0, 100).then((res) => {
      if (res?.data?.content) {
        const activeCountries = res.data.content.filter(country =>
          country?.status === 'ACTIVE' || country?.active === true
        );
        setCountryData(activeCountries);
        setCountryCodeData(activeCountries);
      }
    });
  }, []);

  const fetchCrops = useCallback(async () => {
    const res = await getAllCrops(0, 100);
    if (res?.data?.content) {
      setCropsList(res.data.content);
    }
  }, []);


  useEffect(() => {
    if (type === 'edit') {
      setEditMode(true);
    }
    ecosytemMetaData();
    countriesData();
    fetchCrops();
  }, [type, ecosytemMetaData, countriesData, fetchCrops]);

  useEffect(() => {
    if (type === 'edit') {
      fetchData();
    }
  }, [type, fetchData]);
  
  const handleChange = (event) => {
    const selectedCountryId = event.target.value;
    // Initiatives are offered per country: a new country needs a new pick.
    setValue('initiativeId', '');

    const selectedCountry = countryData.find(c => c.id === selectedCountryId);
    if (selectedCountry) {
      const phoneCode = selectedCountry.phoneCode || '';
      setValue('countryCode', phoneCode, { shouldValidate: true });
      setPhoneNumberMaxLength(getPhoneNumberMaxLength());
    } else {
      setValue('countryCode', '', { shouldValidate: true });
      setPhoneNumberMaxLength(PHONE_MAX_LENGTH);
    }
  };

  const handleCropCheckboxChange = (crop) => {
    setSelectedCrops(prevSelected => {
      const isAlreadySelected = prevSelected.some(c => c.id === crop.id);
      if (isAlreadySelected) {
        return prevSelected.filter(c => c.id !== crop.id);
      } else {
        return [...prevSelected, crop];
      }
    });
  };

  const resetSubUserForm = () => {
    setSubUserForm({ name: '', email: '', phoneNumber: '', countryCode: '' });
    setSubUserErrors({});
  };

  const showError = (message) => {
    setResponseMsg(message);
    setAlert('false');
    setOpenAlert(true);
    setTimeout(() => { alertClose(); }, ALERT_DURATION);
  };

  const validateSubUser = () => {
    const errs = {};
    if (!subUserForm.name.trim()) errs.name = 'Name is required';
    if (subUserForm.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subUserForm.email.trim())) {
      errs.email = 'Invalid email format';
    }
    if (!subUserForm.countryCode) errs.countryCode = 'Country code is required';
    if (!subUserForm.phoneNumber.trim()) errs.phoneNumber = 'Phone Number is required';
    setSubUserErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleAddSubUser = async () => {
    if (!validateSubUser()) return;
    const payload = {
      name: subUserForm.name.trim(),
      email: subUserForm.email.trim(),
      phoneNumber: subUserForm.phoneNumber.trim(),
      countryCode: subUserForm.countryCode || '',
    };

    if (editMode && id) {
      setSubUserAdding(true);
      const res = await addIntegratorSubUser(id, payload);
      setSubUserAdding(false);
      if (res.success) {
        const v = res.data || {};
        setSubUsers(prev => [...prev, {
          id: v.id,
          name: v.integratorName || payload.name,
          email: v.email || payload.email,
          phoneNumber: v.phoneNumber || payload.phoneNumber,
          countryCode: v.countryCode || payload.countryCode,
        }]);
        resetSubUserForm();
      } else {
        showError(res.message || 'Failed to add sub-user');
      }
    } else {
      // Creation mode: the integrator does not exist yet, hold locally and
      // persist after the integrator is created in onSubmit.
      setSubUsers(prev => [...prev, { tempId: `tmp-${Date.now()}`, ...payload }]);
      resetSubUserForm();
    }
  };

  const handleRemoveSubUser = async (subUser) => {
    if (editMode && id && subUser.id) {
      const res = await removeIntegratorSubUser(id, subUser.id);
      if (res.success) {
        setSubUsers(prev => prev.filter(s => s.id !== subUser.id));
      } else {
        showError(res.message || 'Failed to remove sub-user');
      }
    } else {
      const key = subUser.tempId || subUser.id;
      setSubUsers(prev => prev.filter(s => (s.tempId || s.id) !== key));
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      // Map integratorType code to integratorTypeId (integer)
      const integratorTypeId = integratorTypeMap[data.integratorType] || INTEGRATOR_TYPE_IDS[data.integratorType];

      if (!integratorTypeId) {
        setResponseMsg("Invalid integrator type selected");
        setAlert('false');
        setOpenAlert(true);
        setLoading(false);
        return;
      }

      if (showInitiativePicker && !editMode && !data.initiativeId) {
        setError('initiativeId', { type: 'required', message: 'Initiative is required' });
        setLoading(false);
        return;
      }

      // Both spellings, deliberately. Create binds the id; update accepts either and
      // prefers the id, falling back to the code in `integratorType`. Sending both means a
      // stale id cannot silently cost the user their type change.
      const payload = {
        ...data,
        integratorTypeId: integratorTypeId,
      };
      if (data.integratorType !== 'INITIATIVE_BASED' && data.integratorType !== 'FINANCE_BASED') {
        delete payload.appUrl;
      }
      // On edit, only a changed Initiative is sent: the backend then moves the integrator to it.
      if (!showInitiativePicker || initiativeLocked || !data.initiativeId
          || (editMode && data.initiativeId === originalInitiative?.id)) {
        delete payload.initiativeId;
      }

      ecosytemIntegatorCreateOrUpdate(payload, editMode, id).then(async (res) => {
        if (res.statusCode === 201 || res.statusCode === 200) {
          // On create, the response's top-level `id` is the underlying ecosystem's id,
          // not the ecosystem integrator's id — use `ecosystemIntegratorId` instead so
          // crop links (and sub-users below) are stored against the right entity.
          const targetIntegratorId = editMode ? id : res.data?.ecosystemIntegratorId;
          const subUserFailures = [];
          if (targetIntegratorId) {
            try {
              if (editMode) {
                const originalIds = new Set(originalLinkedCrops.map(c => c.id));
                const selectedIds = new Set(selectedCrops.map(c => c.id));
                for (const crop of selectedCrops) {
                  if (!originalIds.has(crop.id)) {
                    await linkCropToIntegrator(crop.id, targetIntegratorId);
                  }
                }
                for (const crop of originalLinkedCrops) {
                  if (!selectedIds.has(crop.id)) {
                    await unlinkCropFromIntegrator(crop.id, targetIntegratorId);
                  }
                }
              } else if (selectedCrops.length > 0) {
                for (const crop of selectedCrops) {
                  await linkCropToIntegrator(crop.id, targetIntegratorId);
                }
              }
            } catch (err) {
              console.error('Error managing crop links:', err);
            }

            // Persist sub-users collected during creation (integrator id now exists)
            if (!editMode && subUsers.length > 0) {
              for (const su of subUsers) {
                const subUserRes = await addIntegratorSubUser(targetIntegratorId, {
                  name: su.name,
                  email: su.email,
                  phoneNumber: su.phoneNumber,
                  countryCode: su.countryCode || '',
                });
                if (!subUserRes.success) {
                  subUserFailures.push(`${su.name}: ${subUserRes.message || 'Failed to add sub-user'}`);
                }
              }
            }
          }

          if (subUserFailures.length > 0) {
            setResponseMsg(`${res.message} However, the following sub-user(s) could not be added: ${subUserFailures.join('; ')}`);
            setAlert('false');
          } else {
            setResponseMsg(res.message);
            setAlert('true');
          }
          setOpenAlert(true)
          setLoading(false)
          setTimeout(() => {
            editMode
              ? navigate(`/ecosystemPartnersView/${id}?type=edit`)
              : navigate('/ecosystemPartners');
            alertClose();
          }, ALERT_DURATION);
        } else if (res.statusCode === 404) {
          setResponseMsg(res.message);
          setAlert('false');
          setOpenAlert(true)
          setLoading(false)
          setTimeout(() => {
            alertClose();
          }, ALERT_DURATION);
        } else if (res.statusCode === 500 || res.statusCode === 400 || res.statusCode === 429) {
          setResponseMsg(res.message);
          setAlert('false');
          setOpenAlert(true)
          setLoading(false)
          setTimeout(() => {
            alertClose();
          }, ALERT_DURATION);
        }
        else {
          setResponseMsg(res.message);
          setAlert('false');
          setLoading(false)
          setOpenAlert(true)
          setTimeout(() => {
            alertClose()
          }, ALERT_DURATION);
        }
      })
    } catch (error) {
      setResponseMsg('Something went wrong. Please try again later.');
      setAlert('false');
      setOpenAlert(true);
      setLoading(false);
      setTimeout(() => {
        alertClose();
      }, ALERT_DURATION);
    }
  };



  return (
    <>
      <Box sx={createEditPageShellSx}>
        <Box sx={createEditPageHeaderSx}>
          <Button
            onClick={() => {
              editMode
                ? navigate(`/ecosystemPartnersView/${id}?type=edit`)
                : navigate('/ecosystemPartners');
            }}
            startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
            sx={createEditBackButtonSx}
          >
            Back
          </Button>
          <Box>
            <Typography className="app-page-title" sx={createEditPageTitleSx}>
              {editMode ? 'Update Ecosystem Integrator' : 'Create Ecosystem Integrator'}
            </Typography>
            <Typography sx={createEditPageSubtitleSx}>
              {ECOSYSTEMCREATION.PARAGRAPH}
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit(onSubmit)} className="material-form">
          <Box sx={createEditCardSx}>
            <Typography sx={sectionTitleSx}>Integrator Details</Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <FieldLabel label={ECOSYSTEMCREATION.LABEL_ONE} required />
                <FormControl fullWidth sx={helperTextSx}>
                  <Controller
                    name="ecosystemIntegratorName"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="ecosystemIntegratorName"
                        placeholder="Enter integrator name"
                        onChange={(e) => {
                          const value = e.target.value;
                          const onlyText = value.replace(/[^a-zA-Z\s]/g, '');
                          field.onChange(onlyText);
                        }}
                        type="text"
                        variant="outlined"
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...fieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <FieldLabel label={ECOSYSTEMCREATION.LABEL_THREE} required />
                <FormControl fullWidth error={!!errors.integratorType} sx={helperTextSx}>
                  <Controller
                    name="integratorType"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        {...field}
                        displayEmpty
                        MenuProps={MenuProps}
                        IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                        sx={fieldSx}
                        onChange={(e) => {
                          field.onChange(e);
                          if (e.target.value !== 'INITIATIVE_BASED') {
                            setValue('initiativeId', '');
                            clearErrors('initiativeId');
                          }
                        }}
                        renderValue={(selected) => !selected ? <Box sx={placeholderTextSx}>{ECOSYSTEMCREATION.SELECT_LABEL_TWO}</Box> : integatorType[selected] ?? selected}
                      >
                        {Object.entries(integatorType).map(([code, name]) => (
                          <MenuItem key={code} value={code}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {!!errors.integratorType && (<FormHelperText error>{errors.integratorType.message}</FormHelperText>)}
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <FieldLabel label={ECOSYSTEMCREATION.LABEL_TWO} required />
                <FormControl fullWidth error={!!errors.countryId} sx={helperTextSx}>
                  <Controller
                    name="countryId"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        {...field}
                        displayEmpty
                        MenuProps={MenuProps}
                        IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                        sx={fieldSx}
                        onChange={(e) => {
                          field.onChange(e);
                          handleChange(e);
                        }}
                        renderValue={(selected) => !selected ? <Box sx={placeholderTextSx}>{ECOSYSTEMCREATION.SELECT_LABEL_ONE}</Box> : countryData.find((item) => item.id === selected)?.name ?? ''}
                      >
                        {countryData.map((data) => (
                          <MenuItem key={data.id} value={data.id}>
                            {data.name}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {!!errors.countryId && (<FormHelperText error>{errors.countryId.message}</FormHelperText>)}
                </FormControl>
              </Grid>

              {showInitiativePicker && (
                <Grid size={{ xs: 12, lg: 6 }}>
                  <FieldLabel label="Initiative" required={!editMode || Boolean(originalInitiative)} />
                  <FormControl fullWidth error={!!errors.initiativeId} sx={helperTextSx}>
                    <Controller
                      name="initiativeId"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <Select
                          {...field}
                          displayEmpty
                          disabled={!selectedCountryId || initiativeOptionsLoading || initiativeLocked}
                          MenuProps={MenuProps}
                          IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                          sx={fieldSx}
                          onChange={(e) => {
                            field.onChange(e);
                            clearErrors('initiativeId');
                          }}
                          renderValue={(selected) => initiativeLocked
                            ? currentInitiatives.map((item) => item.name).join(', ')
                            : !selected
                              ? <Box sx={placeholderTextSx}>{selectedCountryId ? 'Select initiative' : 'Select a country first'}</Box>
                              : initiativeOptions.find((item) => item.id === selected)?.name ?? ''}
                        >
                          {initiativeOptions.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.name}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    />
                    {errors.initiativeId ? (
                      <FormHelperText error>{errors.initiativeId.message}</FormHelperText>
                    ) : initiativeLocked ? (
                      <FormHelperText>
                        On {currentInitiatives.length} Initiatives. Attach or detach it on each Initiative's page.
                      </FormHelperText>
                    ) : originalInitiative && selectedInitiativeId && selectedInitiativeId !== originalInitiative.id ? (
                      <FormHelperText sx={{ color: 'warning.main' }}>
                        Saving moves it from {originalInitiative.name} to{' '}
                        {initiativeOptions.find((item) => item.id === selectedInitiativeId)?.name}. Areas that approved
                        it for {originalInitiative.name} decide again for the new Initiative.
                      </FormHelperText>
                    ) : (
                      selectedCountryId && !initiativeOptionsLoading && initiativeOptions.length === 0 && (
                        <FormHelperText>No Initiative is offered in this country yet. Create one under Initiatives first.</FormHelperText>
                      )
                    )}
                  </FormControl>
                </Grid>
              )}

              <Grid size={{ xs: 12, lg: 6 }}>
                <FieldLabel label={ECOSYSTEMCREATION.LABEL_FIVE} required />
                <FormControl fullWidth sx={helperTextSx}>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="email"
                        placeholder="Enter email address"
                        type="text"
                        variant="outlined"
                        error={!!error}
                        helperText={error ? error.message : ''}
                        sx={{ ...fieldSx, ...helperTextSx }}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, lg: 6 }}>
                <FieldLabel label={ECOSYSTEMCREATION.LABEL_SIX} required />
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth error={!!errors.countryCode} sx={helperTextSx}>
                      <Controller
                        name="countryCode"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <Select
                            {...field}
                            displayEmpty
                            MenuProps={MenuProps}
                            IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                            sx={fieldSx}
                            onChange={(e) => {
                              field.onChange(e);
                              setPhoneNumberMaxLength(getPhoneNumberMaxLength(e.target.value));
                            }}
                            renderValue={(selected) => {
                              if (!selected) {
                                return <Box sx={placeholderTextSx}>{ECOSYSTEMCREATION.SELECT_LABEL_THREE}</Box>;
                              }
                              const selectedCountryCode = countryCodeData.find((item) => item.phoneCode === selected);
                              return selectedCountryCode ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <FlagImage flagUrl={selectedCountryCode.countryFlag} size={20} />
                                  <span>{selectedCountryCode.phoneCode}</span>
                                </Box>
                              ) : selected;
                            }}
                          >
                            {countryCodeData.map((data) => (
                              <MenuItem key={`${data.id ?? data.name}-${data.phoneCode}`} value={data.phoneCode}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                  <FlagImage flagUrl={data.countryFlag} size={20} />
                                  <span>{data.phoneCode}</span>
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                      {!!errors.countryCode && (<FormHelperText error>{errors.countryCode.message}</FormHelperText>)}
                    </FormControl>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 8 }}>
                    <FormControl fullWidth sx={helperTextSx}>
                      <Controller
                        name="phoneNumber"
                        control={control}
                        defaultValue=""
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            id="phoneNumber"
                            placeholder="Enter phone number"
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9]/g, '');
                              if (e.target.value.length > phoneNumberMaxLength) {
                                e.target.value = e.target.value.slice(0, phoneNumberMaxLength);
                              }
                            }}
                            inputProps={{ maxLength: phoneNumberMaxLength }}
                            type="text"
                            variant="outlined"
                            error={!!error}
                            helperText={error ? error.message : ""}
                            sx={{ ...fieldSx, ...helperTextSx }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FieldLabel label="Partnership Ecosystem" />
                <Controller
                  name="isOpen"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: field.value ? 400 : 600,
                            color: field.value ? 'text.disabled' : '#22367A',
                          }}
                        >
                          Closed ecosystem
                        </Typography>
                        <AntSwitch
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          inputProps={{ 'aria-label': 'Open or closed ecosystem' }}
                        />
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: field.value ? 600 : 400,
                            color: field.value ? '#22367A' : 'text.disabled',
                          }}
                        >
                          Open ecosystem
                        </Typography>
                      </Box>
                      <Typography sx={{ color: 'text.disabled', fontSize: '0.75rem', mt: 0.5 }}>
                        Open is the default for Area Based integrators, Closed for the others
                      </Typography>
                    </Box>
                  )}
                />
              </Grid>

              {showRedirectUrl && (
                <Grid size={{ xs: 12 }}>
                  <FieldLabel label={showInitiativePicker ? 'eK Link login Redirect URL (only to override)' : 'eK Link login Redirect URL'} />
                  <FormControl fullWidth sx={helperTextSx}>
                    <Controller
                      name="appUrl"
                      control={control}
                      defaultValue=""
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          id="appUrl"
                          placeholder="https://app.example.com/login"
                          type="text"
                          variant="outlined"
                          error={!!error}
                          helperText={
                            error
                              ? error.message
                              : showInitiativePicker
                                ? (inheritedRedirect
                                  ? `Leave blank to use ${inheritedRedirect.url} (${inheritedRedirect.source}, set in Settings → Redirect Configuration).`
                                  : 'Leave blank to use the Initiative\'s URL once it is set in Settings → Redirect Configuration.')
                                : 'Where this integrator is sent after eK Link OTP. Leave blank to use the Initiative Based type URL in Settings → Redirect Configuration.'
                          }
                          sx={{ ...fieldSx, ...helperTextSx }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>

          <Box sx={{ ...createEditCardSx, mt: 3 }}>
            <Typography sx={sectionTitleSx}>Sub-Users</Typography>
            <Typography sx={{ ...createEditPageSubtitleSx, mb: 2 }}>
              Add additional users to this ecosystem integrator
            </Typography>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <FieldLabel label="Name" required />
                <FormControl fullWidth sx={helperTextSx}>
                  <TextField
                    placeholder="Enter name"
                    value={subUserForm.name}
                    onChange={(e) => setSubUserForm(f => ({ ...f, name: e.target.value.replace(/[^a-zA-Z\s]/g, '') }))}
                    type="text"
                    variant="outlined"
                    error={!!subUserErrors.name}
                    helperText={subUserErrors.name || ''}
                    sx={{ ...fieldSx, ...helperTextSx }}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <FieldLabel label="Email (Optional)" />
                <FormControl fullWidth sx={helperTextSx}>
                  <TextField
                    placeholder="Enter email address"
                    value={subUserForm.email}
                    onChange={(e) => setSubUserForm(f => ({ ...f, email: e.target.value }))}
                    type="text"
                    variant="outlined"
                    error={!!subUserErrors.email}
                    helperText={subUserErrors.email || ''}
                    sx={{ ...fieldSx, ...helperTextSx }}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 8, lg: 4 }}>
                <FieldLabel label="Phone Number" required />
                <Grid container spacing={1}>
                  <Grid size={{ xs: 5, sm: 4 }}>
                    <FormControl fullWidth error={!!subUserErrors.countryCode} sx={helperTextSx}>
                      <Select
                        displayEmpty
                        value={subUserForm.countryCode}
                        MenuProps={MenuProps}
                        IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />}
                        sx={fieldSx}
                        onChange={(e) => setSubUserForm(f => ({ ...f, countryCode: e.target.value }))}
                        renderValue={(selected) => {
                          if (!selected) {
                            return <Box sx={{ ...placeholderTextSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Code</Box>;
                          }
                          const selectedCountryCode = countryCodeData.find((item) => item.phoneCode === selected);
                          return selectedCountryCode ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FlagImage flagUrl={selectedCountryCode.countryFlag} size={20} />
                              <span>{selectedCountryCode.phoneCode}</span>
                            </Box>
                          ) : selected;
                        }}
                      >
                        {countryCodeData.map((data) => (
                          <MenuItem key={`sub-${data.id ?? data.name}-${data.phoneCode}`} value={data.phoneCode}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <FlagImage flagUrl={data.countryFlag} size={20} />
                              <span>{data.phoneCode}</span>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {!!subUserErrors.countryCode && (<FormHelperText error>{subUserErrors.countryCode}</FormHelperText>)}
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 7, sm: 8 }}>
                    <FormControl fullWidth sx={helperTextSx}>
                      <TextField
                        placeholder="Enter phone number"
                        value={subUserForm.phoneNumber}
                        onChange={(e) => {
                          const max = getPhoneNumberMaxLength(subUserForm.countryCode);
                          setSubUserForm(f => ({ ...f, phoneNumber: e.target.value.replace(/[^0-9]/g, '').slice(0, max) }));
                        }}
                        type="text"
                        variant="outlined"
                        error={!!subUserErrors.phoneNumber}
                        helperText={subUserErrors.phoneNumber || ''}
                        sx={{ ...fieldSx, ...helperTextSx }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              <Grid size={{ xs: 12, sm: 4, lg: 2 }}>
                <Box aria-hidden sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <FieldLabel label={' '} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 48 }}>
                  <Button
                    variant="outlined"
                    onClick={handleAddSubUser}
                    disabled={subUserAdding || loading}
                    startIcon={<Icon icon="mdi:plus" width="20" height="20" />}
                    sx={{ width: { xs: '100%', sm: 'auto' }, height: 48, textTransform: 'none', borderColor: '#22367A', color: '#22367A' }}
                  >
                    Add
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {subUsers.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                {subUsers.map((su) => (
                  <Box
                    key={su.id || su.tempId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: '1px solid #E0E0E0',
                      borderRadius: '8px',
                      p: 1.5,
                      mb: 1,
                      backgroundColor: '#FAFAFA',
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{su.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        {su.email}{su.email && (su.phoneNumber || su.countryCode) ? ' · ' : ''}{su.countryCode ? `${su.countryCode} ` : ''}{su.phoneNumber}
                      </Typography>
                    </Box>
                    <IconButton onClick={() => handleRemoveSubUser(su)} disabled={loading} size="small">
                      <Icon icon="mdi:delete-outline" width="20" height="20" color="#d32f2f" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <FormHelperText sx={{ mt: 1 }}>No sub-users added yet</FormHelperText>
            )}
          </Box>

          <Box sx={{ ...createEditCardSx, mt: 3 }}>
            <Box
              onClick={() => setCropsExpanded(prev => !prev)}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            >
              <Typography sx={{ ...sectionTitleSx, mb: 0 }}>Link Crops</Typography>
              <Icon icon={cropsExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} width="24" height="24" color={primaryIconColor} />
            </Box>
            <Collapse in={cropsExpanded}>
            <Grid container spacing={3} sx={{ mt: 0 }}>
              <Grid size={12}>
                <FieldLabel label="Select Crops to Link (Optional)" />
                <Box sx={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  padding: '16px',
                  backgroundColor: '#FAFAFA',
                  maxHeight: '400px',
                  overflowY: 'auto',
                }}>
                  {cropsList.length > 0 ? (
                    <Box>
                      {Object.entries(
                        cropsList.reduce((acc, crop) => {
                          const category = crop.category?.replace(/_/g, ' ') || 'Uncategorized';
                          if (!acc[category]) {
                            acc[category] = [];
                          }
                          acc[category].push(crop);
                          return acc;
                        }, {})
                      )
                        .sort(([categoryA], [categoryB]) => categoryA.localeCompare(categoryB))
                        .map(([category, crops]) => (
                          <Box key={category} sx={{ mb: 4 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: '#1C1B1F',
                                mb: 2,
                                fontSize: '14px',
                                textTransform: 'capitalize',
                              }}
                            >
                              {category}
                            </Typography>
                            <Grid container spacing={2} sx={{ mt: 0 }}>
                              {crops.map((crop) => (
                                <Grid key={crop.id} size={{ xs: 12, sm: 6, md: 3 }}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedCrops.some(c => c.id === crop.id)}
                                        onChange={() => handleCropCheckboxChange(crop)}
                                        disabled={loading}
                                        size="small"
                                      />
                                    }
                                    label={
                                      <Typography variant="body2">
                                        {crop.name}
                                      </Typography>
                                    }
                                    sx={{ width: '100%' }}
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        ))
                      }
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#999' }}>
                      No crops available
                    </Typography>
                  )}
                </Box>
                <FormHelperText>
                  {selectedCrops.length > 0
                    ? `${selectedCrops.length} crop${selectedCrops.length !== 1 ? 's' : ''} selected`
                    : editMode
                      ? 'No crops linked to this integrator'
                      : 'Select crops to link with this integrator during creation (optional)'}
                </FormHelperText>
              </Grid>
            </Grid>
            </Collapse>
          </Box>

          {editMode && id && (
            <LinkedAccounts
              integratorId={id}
              cardSx={{ ...createEditCardSx, mt: 3 }}
              titleSx={sectionTitleSx}
              subtitleSx={createEditPageSubtitleSx}
            />
          )}

          <Box sx={{ ...createEditCardSx, mt: 3 }}>
            <Box sx={{ ...createEditFooterSx, mt: 0, pt: 0, borderTop: 'none', flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
              <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
                <Button
                  variant="text"
                  onClick={() => { navigate('/ecosystemPartners') }}
                  sx={{
                    ...createEditCancelButtonSx,
                    px: 2,
                    visibility: editMode ? 'hidden' : 'visible',
                    width: { xs: '100%', sm: 'auto' },
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                  }}
                >
                  Cancel
                </Button>
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, ml: { sm: 'auto' } }}>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{
                    ...createEditPrimaryButtonSx,
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': { backgroundColor: '#22367A', boxShadow: 'none' },
                  }}
                >
                  {editMode ? "Update Integrator" : "Create Integrator"}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
      </Box>

      {loading && <Loader />}

      <Box textAlign={'end'}>
        <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
        <AlertToast open={openAlert && alert === 'false'} onClose={alertClose} message={responseMsg} variant="error" />
      </Box>
    </>
  )
}

